/**
 * Block renderer registry
 *
 * Central registry for block renderers. Supports both built-in blocks
 * and customer-registered custom blocks.
 *
 * Usage for customers:
 * ```javascript
 * window.PromptMySite.registerBlock('my-custom-block', (data, container, onAction) => {
 *   const wrapper = document.createElement('div');
 *   wrapper.className = 'my-custom-block';
 *   wrapper.textContent = data.content;
 *   container.appendChild(wrapper);
 * });
 * ```
 */

import type { BlockRenderer, BlockRendererContext, MessageBlock } from './types';
import type { ApiClient } from '../../core/api-client';
import { escapeHtml, createErrorState } from './utils';
import { initBlockEffects } from '../effects';

// =============================================================================
// CRASH PROTECTION - Prevents browser crashes from runaway rendering
// =============================================================================

/** Maximum nesting depth for blocks (tabs > blocks > tabs > etc.) */
const MAX_BLOCK_DEPTH = 5;

/** Maximum total blocks per message render cycle */
const MAX_BLOCKS_PER_RENDER = 100;

/** Maximum items in any array (cards, team members, etc.) */
export const MAX_ARRAY_ITEMS = 50;

/** Current render depth tracker (reset between messages) */
let currentRenderDepth = 0;

/** Total blocks rendered in current cycle */
let blocksRenderedInCycle = 0;

/** Timestamp of render cycle start */
let renderCycleStart = 0;

/** Maximum time allowed for a render cycle (15 seconds - accounts for async API fetches) */
const MAX_RENDER_TIME_MS = 15000;

/** Track if we've already logged a crash protection error this cycle (prevent console spam) */
let crashProtectionLogged = false;

/**
 * Reset render cycle counters.
 * Called at the start of rendering a new message.
 * Timer starts lazily on first block render, not at stream start.
 */
export function resetRenderCycle(): void {
  currentRenderDepth = 0;
  blocksRenderedInCycle = 0;
  renderCycleStart = 0; // Don't start timer - wait for first block
  crashProtectionLogged = false;
}

/**
 * Check if rendering should continue or be blocked for safety.
 *
 * Timer behavior:
 * - Top-level blocks (depth 0) reset the timer, so each gets a fresh 15s window
 * - Nested blocks share their parent's timer to prevent infinite nesting
 * - This allows slow-streaming responses while still catching runaway renders
 */
function shouldBlockRender(): { blocked: boolean; reason?: string } {
  // Reset timer for each top-level block - this allows slow streaming responses
  // while still protecting against infinite recursion within a single block
  if (currentRenderDepth === 0) {
    renderCycleStart = Date.now();
  } else if (renderCycleStart === 0) {
    // Fallback: start timer if not already running (shouldn't happen normally)
    renderCycleStart = Date.now();
  }

  if (currentRenderDepth > MAX_BLOCK_DEPTH) {
    return { blocked: true, reason: `Maximum nesting depth (${MAX_BLOCK_DEPTH}) exceeded` };
  }
  if (blocksRenderedInCycle > MAX_BLOCKS_PER_RENDER) {
    return { blocked: true, reason: `Maximum blocks per message (${MAX_BLOCKS_PER_RENDER}) exceeded` };
  }
  if (Date.now() - renderCycleStart > MAX_RENDER_TIME_MS) {
    return { blocked: true, reason: `Render time exceeded (${MAX_RENDER_TIME_MS}ms)` };
  }
  return { blocked: false };
}

/**
 * Limit array size to prevent DOM explosion.
 * Use this in block renderers when iterating arrays.
 */
export function limitArray<T>(arr: T[], limit: number = MAX_ARRAY_ITEMS): T[] {
  if (!Array.isArray(arr)) return [];
  if (arr.length <= limit) return arr;
  console.warn(`[BlockSafety] Array truncated from ${arr.length} to ${limit} items`);
  return arr.slice(0, limit);
}

// =============================================================================
// REGISTRY
// =============================================================================

/**
 * Registry of block type renderers.
 * Key is lowercase block type, value is renderer function.
 */
const blockRenderers: Map<string, BlockRenderer> = new Map();

/**
 * Register a block renderer.
 *
 * @param type - Block type name (case-insensitive)
 * @param renderer - Renderer function
 *
 * @example
 * registerBlockRenderer('my-block', (data, container, onAction) => {
 *   container.innerHTML = `<div>${data.title}</div>`;
 * });
 */
export function registerBlockRenderer(type: string, renderer: BlockRenderer): void {
  blockRenderers.set(type.toLowerCase(), renderer);
}

/**
 * Get a block renderer by type.
 *
 * @param type - Block type name (case-insensitive)
 * @returns Renderer function or undefined if not found
 */
export function getBlockRenderer(type: string): BlockRenderer | undefined {
  return blockRenderers.get(type.toLowerCase());
}

/**
 * Check if a block type is registered.
 *
 * @param type - Block type name (case-insensitive)
 */
export function hasBlockRenderer(type: string): boolean {
  return blockRenderers.has(type.toLowerCase());
}

/**
 * Get all registered block types.
 */
export function getRegisteredBlockTypes(): string[] {
  return Array.from(blockRenderers.keys());
}

// =============================================================================
// CONTEXT (for data-driven blocks)
// =============================================================================

const blockContext: BlockRendererContext = {
  apiClient: null,
  workspaceId: null,
  currencyCode: 'GBP',
  currencySymbol: '£',
  availableFlows: [],
  rootElement: null,
  vertical: undefined,
};

/**
 * Set the block renderer context.
 * Called by widget during initialization.
 *
 * @param apiClient - API client for data fetching
 * @param workspaceId - Workspace ID
 * @param currencyCode - ISO 4217 currency code (default: GBP)
 * @param currencySymbol - Currency symbol (default: £)
 * @param availableFlows - List of available flow IDs (default: empty)
 * @param rootElement - Shadow root for rendering modals inside shadow DOM
 * @param vertical - Business vertical for industry-specific block rendering
 */
export function setBlockRendererContext(
  apiClient: ApiClient,
  workspaceId: string,
  currencyCode: string = 'GBP',
  currencySymbol: string = '£',
  availableFlows: string[] = [],
  rootElement: ShadowRoot | HTMLElement | null = null,
  vertical?: string
): void {
  blockContext.apiClient = apiClient;
  blockContext.workspaceId = workspaceId;
  blockContext.currencyCode = currencyCode;
  blockContext.currencySymbol = currencySymbol;
  blockContext.availableFlows = availableFlows;
  blockContext.rootElement = rootElement;
  blockContext.vertical = vertical;
}

/**
 * Get the current block renderer context.
 * Used by data-driven blocks (flow-form, live-section) to access API.
 */
export function getBlockRendererContext(): BlockRendererContext {
  return blockContext;
}

// =============================================================================
// MAIN RENDER FUNCTION
// =============================================================================

/**
 * Render a block into a container.
 *
 * @param block - The block to render (type + data)
 * @param container - DOM element to render into
 * @param onAction - Callback for action events
 */
export function renderBlock(
  block: MessageBlock,
  container: HTMLElement,
  onAction?: (value: string) => void
): void {
  // CRASH PROTECTION: Check if we should block this render
  const safety = shouldBlockRender();
  if (safety.blocked) {
    // Only log once per cycle to prevent console spam (can be 26k+ attempts)
    if (!crashProtectionLogged) {
      console.error(`[renderBlock] BLOCKED: ${safety.reason}`);
      renderError(block.type, 'Content too complex to display safely', container);
      crashProtectionLogged = true;
    }
    return;
  }

  // Track render depth for nested blocks
  currentRenderDepth++;
  blocksRenderedInCycle++;

  const renderer = getBlockRenderer(block.type);

  try {
    if (renderer) {
      // Validate that block.data is an object (not null, not primitive)
      if (block.data === null || typeof block.data !== 'object') {
        console.warn(`[renderBlock] Invalid data for ${block.type}: expected object, got ${typeof block.data}`, block.data);
        renderError(block.type, 'Invalid block data format', container);
        return;
      }

      renderer(block.data, container, onAction);

      // Initialize JS-driven effects (tilt, ripple) on rendered block elements
      initBlockEffects(container);
    } else {
      console.warn(`[renderBlock] No renderer registered for block type: ${block.type}`);
      // Fallback: render as JSON for debugging
      renderFallback(block, container);
    }
  } catch (error) {
    console.error(`[renderBlock] Exception rendering ${block.type}:`, error, 'Block data:', block.data);
    renderError(block.type, error instanceof Error ? error.message : 'Render failed', container);
  } finally {
    // Always decrement depth after render completes
    currentRenderDepth--;
  }
}

/**
 * Fallback renderer for unknown block types.
 * Shows the block type and data as JSON.
 */
function renderFallback(block: MessageBlock, container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-block pms-block-unknown';
  wrapper.innerHTML = `
    <div class="pms-block-label">Unknown block: ${escapeHtml(block.type)}</div>
    <pre class="pms-block-json">${escapeHtml(JSON.stringify(block.data, null, 2))}</pre>
  `;
  container.appendChild(wrapper);
}

/**
 * Error renderer for block rendering failures.
 * Shows a styled error message instead of breaking the UI.
 */
function renderError(_blockType: string, _errorMessage: string, container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-block';
  wrapper.innerHTML = createErrorState(`This content could not be displayed`, { compact: true });
  container.appendChild(wrapper);
}
